import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Iconify from "../../../../components/iconify";
import { useLocales } from "../../../../locales";
import { useTheme } from "@mui/material/styles";
import { PrimaryButton, PrimaryLoadingButton } from "../listing-details/listingButtons";
import { useMutationWithAuth, useQueryWithAuth } from "src/graphql/instances";
import { CLEAR_CART_ITEMS, CREATE_CART_ITEMS, DELETE_CART_ITEM } from "src/graphql/mutations";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { GET_CART, GET_EVENT } from "../../../../graphql/queries";
import { useLocation, useParams } from "react-router";
import EventImageLayout from "src/layouts/event";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { getTicketCard } from "../tickets/TicketShopCard";
import StickyFooter from "../../../../components/StickyFooter";
import { useAvailableEventModules } from "../../../../hooks/useAvailableEventModules";
import { CREATE_BOOKING_HOLDING_FEE_USER } from "../../../../graphql/mutations/bookings";
import ProductCard from "../shop/ProductCard";
import { Incrementer } from "../shop";
import BookingPage from "../booking/BookingPage";
import { fCurrency } from "../../../../utils/formatNumber";
import { formatCategoryTitle } from "../../../../utils/bookingModule";
import { useSnackbar } from "notistack";
import { PATHS } from "../../../../routes/paths";
import DeleteForeignTicketsModal from "../listing-details/DeleteForeignTicketsModal";
import { SeatsChart } from "src/components/SeatsChart";
import ReactGA from "react-ga4";
import { GET_MENU_CATEGORIES } from "src/graphql/queries/bookings";
import { currencyMap } from "src/utils/currencies";
import { fCommission } from "src/utils/ticketPriceCalculatorFunctions";
import ChartEventSeatPicker from "src/components/seating-chart/ChartEventSeatPicker";
import { budgetToQuantities } from "src/hooks/useSeatSelection";
import LoadingScreen from "src/components/loading-screen";

BookingWizard.propTypes = {};

const getSeatingMapPricing = (ticketTypes) => {
  if (!ticketTypes) {
    return null;
  }

  return ticketTypes.reduce((acc, ticket) => {
    if (ticket.isPublished) {
      acc.push({
        category: ticket.name,
        price: ticket.price,
      });
    }

    return acc;
  }, []);
};

export default function BookingWizard() {
  const { eventId } = useParams();

  const token = localStorage.getItem("accessToken");
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const { translate, currentLang } = useLocales();
  const { enqueueSnackbar } = useSnackbar();

  const params = new URLSearchParams(window.location.search);
  const embedMode = params.get("embed") === "true";
  const isEmbedded = embedMode || window.self !== window.top;

  const { data: cartData } = useQueryWithAuth(GET_CART, {
    fetchPolicy: "no-cache",
  });
  const [clearCartItems] = useMutationWithAuth(CLEAR_CART_ITEMS);
  const [createCartItems] = useMutationWithAuth(CREATE_CART_ITEMS);
  const [deleteCartItem] = useMutationWithAuth(DELETE_CART_ITEM);
  const [createBookingHoldingFee] = useMutationWithAuth(
    CREATE_BOOKING_HOLDING_FEE_USER
  );
  const [getEvent] = useLazyQuery(GET_EVENT, {
    context: {
      headers: {
        "x-nightz-standard-behaviour": "true",
        "x-nightz-platform": "web",
        Authorization: token,
      },
    },
    variables: { eventId },
  });

  const { data: menuCategoriesData } = useQueryWithAuth(
    GET_MENU_CATEGORIES
  );

  const location = useLocation();

  const [eventData, setEventData] = useState(null);
  const { modules } = useAvailableEventModules(eventData);
  const [activeAccordion, setActiveAccordion] = useState(null);
  const [showBooking, setShowBooking] = useState(location.state?.showBooking || null);
  const [ticketQuantities, updateTicketQuantities] = useState(location.state?.selectedTickets || null);
  const [selectedBookingConfig, setSelectedBookingConfig] = useState(location.state?.selectedBookingConfig || null);
  const [bookingCartItemId, setBookingCartItemId] = useState(null);
  const [selectedMenus, setSelectedMenus] = useState(location.state?.selectedMenus || []);
  const [hasOtherEventItemsInCart, setHasOtherEventItemsInCart] = useState(false);
  const [clearCartModalOpen, setClearCartModalOpen] = useState(false);
  const [showSeatsChart, setShowSeatsChart] = useState(false);
  const [userSelectedAllSeats, setUserSelectedAllSeats] = useState(false);
  const [chart, setChart] = useState(null);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const ticketTypes = eventData?.tickets?.ticketTypes;
  const seatingMapPricing = getSeatingMapPricing(ticketTypes);
  const [expandedMenuCats, setExpandedMenuCats] = useState(new Set());

  const eventHasQuestionnaire = eventData?.questionnaire?.moduleStatus &&
    eventData?.questionnaire?.pages?.some((p) => p.questions?.length > 0);

  const currency = useMemo(() => currencyMap[eventData?.general?.currency], [eventData?.general?.currency]);
  const existingMenuCategories = useMemo(() => menuCategoriesData?.menuCategories || [], [menuCategoriesData]);

  const chartEvent = eventData?.chartEvent ?? null;
  const pickerRef = useRef(null);
  const pickerQuantities = useMemo(() => ticketQuantities ?? [], [ticketQuantities]);

  const maxSelectableObjects = ticketQuantities?.map(({ name, quantity }) => ({
    category: name,
    quantity,
  }));
  const ticketCount = ticketQuantities?.reduce(
    (acc, item) => acc + item.quantity,
    0
  );

  const menuCount = selectedMenus?.reduce(
    (acc, item) => acc + item.quantity,
    0
  );
  const menuCategories = eventData?.booking?.menus
    ? Object.groupBy(eventData.booking.menus, (menu) => menu.category)
    : null;

  const totalCost = useMemo(() => {
    const ticketCost = fCommission(
      (ticketQuantities?.reduce(
        (acc, item) => acc + item.quantity * item.price,
        0
      ) || 0),
      eventData?.tickets.comission,
      eventData?.general?.commissionPaidByUser
    )
    const bookingCost = fCommission(
      (selectedBookingConfig?.holdingFee || 0) +
      (selectedMenus?.reduce(
        (acc, item) => acc + item.quantity * item.price,
        0
      ) || 0),
      eventData?.booking.comissionHoldingFee,
      eventData?.general?.commissionPaidByUser
    )
    return ticketCost + bookingCost;
  }, [ticketQuantities, selectedBookingConfig, selectedMenus, eventData])

  const shouldRedirectToSeats = Boolean(eventData?.general?.seatsIoEventKey || eventData?.chartEvent);
  const hasFloorplan = eventData?.booking.enabledCustomerPlan;

  useEffect(() => {
    (async () => {
      const eventDataRes = await getEvent();
      setEventData(eventDataRes.data.event);
      if (eventDataRes.data.event?.tickets?.ticketTypes?.length > 0) {
        setActiveAccordion("tickets");
      } else {
        setActiveAccordion("booking");
      }
    })();
  }, []);

  useEffect(() => {
    if (activeAccordion !== "bookingMenu") {
      setExpandedMenuCats(new Set());
    }
  }, [activeAccordion]);

  useEffect(() => {
    const itemsToOtherEvent = cartData?.cart?.items.filter(
      (it) =>
        (it.ticketType && it.ticketType?.eventId !== eventId) ||
        (it.bookingModule &&
          it.bookingModule?.id !== eventData?.booking?.id)
    );
    setHasOtherEventItemsInCart(!!itemsToOtherEvent?.length);

    const itemsToCurrentEvent = cartData?.cart?.items.filter(
      (it) =>
        (it.ticketType && it.ticketType?.eventId === eventId) ||
        (it.bookingModule && it.bookingModule.id === eventData?.booking?.id)
    );
    const bookingCartItem = itemsToCurrentEvent?.find(
      (it) => !!it?.metadata?.booking
    );
    if (bookingCartItem) {
      const bookingToSet = location.state?.selectedBookingConfig ? location.state.selectedBookingConfig : bookingCartItem.metadata.booking;
      setSelectedBookingConfig(bookingToSet);
      setBookingCartItemId(bookingCartItem.id);
    }

    const bookingMenuCartItems = itemsToCurrentEvent?.filter(
      (it) => !!it?.metadata?.bookingMenu
    );

    setSelectedMenus(
      location.state?.selectedMenus
        ? location.state.selectedMenus
        : bookingMenuCartItems
          ? bookingMenuCartItems.map((it) => ({
            ...it.metadata.bookingMenu,
            quantity: it.quantity,
          }))
          : []
    );
  }, [cartData]);

  const doBuy = useCallback(
    async (skipCheckingOtherCartItems = false) => {
      if (isLoadingButton) return;
      setIsLoadingButton(true);
      try {
        if (!skipCheckingOtherCartItems && hasOtherEventItemsInCart) {
          setClearCartModalOpen(true);
          setIsLoadingButton(false);
          return;
        }

        await clearCartItems({ variables: { keepSeatHolds: true } })

        const chartItems = chartEvent ? pickerRef.current?.getCartItems() : null;
        if (chartEvent && !chartItems?.length) {
          setIsLoadingButton(false);
          return; // picker not mounted or nothing selected
        }

        let completeCartItems;
        if (chartItems) {
          completeCartItems = chartItems; // [{ id, name, price, quantity, seatIds, seatsHoldToken }]
          // The cart now owns these holds. Must happen before anything below that
          // navigates or hides the seat view, or unmounting the picker would release the seats.
          pickerRef.current?.commit();
        } else {
          // existing Seats.io logic, unchanged
          const selectedSeats = await chart?.listSelectedObjects();
          completeCartItems = ticketQuantities?.map((ticket) => ({
            ...ticket,
            ...(chart
              ? {
                seatsHoldToken: chart.holdToken,
                seatIds: selectedSeats
                  .filter((seat) => seat.category.label === ticket.name)
                  .map((seat) => seat.seatId),
              }
              : {}),
          }));
        }

        if (eventHasQuestionnaire) {
          navigate(PATHS.questionnaire.new(eventId), {
            state: {
              eventData,
              pendingCartItems: completeCartItems.map((it) => ({
                id: it.id,
                quantity: it.quantity,
                seatIds: it.seatIds,
                seatsHoldToken: it.seatsHoldToken,
              })),
              pendingBooking: selectedBookingConfig
                ? {
                  ...selectedBookingConfig,
                  ...(selectedMenus?.length
                    ? { bookingMenus: selectedMenus.map((it) => ({ id: it.id, quantity: it.quantity })) }
                    : {}),
                }
                : null,
              checkoutUrl: PATHS.listings.checkout + (isEmbedded ? "?embed=true" : ""),
            },
          });
          setIsLoadingButton(false);
          return;
        }

        if (ticketQuantities?.length) {
          await createCartItems({
            variables: {
              cartItems: completeCartItems.map((it) => ({
                id: it.id,
                quantity: it.quantity,
                seatIds: it.seatIds,
                seatsHoldToken: it.seatsHoldToken,
              })),
            },
          });
          await pickerRef.current?.releaseDeferred();
        }

        if (selectedBookingConfig) {
          let booking = selectedBookingConfig;
          if (selectedMenus?.length) {
            booking["bookingMenus"] = selectedMenus.map((it) => ({
              id: it.id,
              quantity: it.quantity,
            }));
          }
          await createBookingHoldingFee({
            variables: { metadata: { booking } },
          });
        }
        if (showSeatsChart) {
          setShowSeatsChart(false);
        }
        sendGAEvent(completeCartItems);
        navigate(PATHS.listings.checkout + (isEmbedded ? '?embed=true' : ''));
        setIsLoadingButton(false);
      } catch (err) {
        console.error(err);
        setIsLoadingButton(false);
        enqueueSnackbar(translate("productPage.genericError"), {
          variant: "error",
          autoHideDuration: 5000,
        });
      }
    },
    [
      ticketQuantities,
      selectedBookingConfig,
      selectedMenus,
      chart,
      showSeatsChart,
      hasOtherEventItemsInCart,
      chartEvent,
      eventData
    ]
  );


  const sendGAEvent = (completeCartItems) => {
    const selected_tickets = completeCartItems?.map((it) => ({
      item_id: it.id,
      item_name: it.name,
      item_list_id: eventData?.general?.id,
      item_list_name: eventData?.general?.id,
      price: it.price,
      quantity: it.quantity
    })) || [];
    const selected_booking = selectedBookingConfig ? [{
      item_id: selectedBookingConfig.areaId,
      item_name: eventData?.booking?.areas?.find((it) => it.id === selectedBookingConfig.areaId)?.name,
      item_list_id: eventData?.general?.id,
      item_list_name: eventData?.general?.id,
      price: selectedBookingConfig.holdingFee,
      quantity: 1
    }] : []
    const selected_menus = selectedMenus?.map((it) => ({
      item_id: it.id,
      item_name: it.name,
      item_list_id: eventData?.general?.id,
      item_list_name: eventData?.general?.id,
      price: it.price,
      quantity: it.quantity
    })) || [];
    ReactGA.event("add_to_cart", {
      currency: eventData?.general?.currency,
      value: fCurrency(totalCost),
      item_list_id: eventData?.general?.id,
      item_list_name: eventData?.general?.name,
      items: [...selected_tickets, ...selected_booking, ...selected_menus]
    });
    // fbq("track", "AddToCart", {
    //   currency: eventData?.general?.currency,
    //   value: fCurrency(totalCost),
    //   content_name: eventData?.general?.name,
    //   content_ids: [...selected_tickets, ...selected_booking, ...selected_menus].map(
    //     (it) => it.item_id ?? it.id
    //   ),
    //   contents: [...selected_tickets, ...selected_booking, ...selected_menus].map((it) => ({
    //     id: it.item_id ?? it.id,
    //     quantity: it.quantity ?? 1,
    //     item_price: it.price ?? it.item_price,
    //   })),
    // });
  }

  const removeHandler = () => {
    setShowBooking(false);
    setSelectedBookingConfig(null);
    setSelectedMenus([]);
    if (bookingCartItemId) {
      deleteCartItem({
        variables: {
          cartItemId: bookingCartItemId,
        }
      });
    }
  }

  const handleSelectArea = (id) => {
    if (!eventData?.booking?.enabledCustomerPlan || !isMobile) {
      setShowBooking(id);
    } else {
      navigate(PATHS.booking.floorMap(eventData?.booking.id) + (isEmbedded ? '?embed=true' : ''), {
        state: {
          eventId: eventData?.general.id,
          isReservationsOnly: false,
          isBookableUnit: eventData?.booking.isBookableUnit,
          selectedArea: { label: "Entrance", value: "Entrance" },
          eventData: eventData?.general,
          formData: { area: id }
        }
      })
    }
  }

  const toggleMenuCategory = (cat) => {
    setExpandedMenuCats((prev) => {
      const next = new Set(prev);
      next.has(cat) ? next.delete(cat) : next.add(cat);
      return next;
    });
  };

  const getMenuCategoryTitle = (menuCategory) => {
    const matchingCategory = existingMenuCategories.find(c => c.key === menuCategory);
    if (!matchingCategory) return formatCategoryTitle(
      translate(`eventManager.menus.category.${menuCategory}`),
      menuCategory
    );
    return currentLang.value === "ro" ? matchingCategory.nameRo : matchingCategory.nameEn;
  }

  if (!eventData) {
    return <LoadingScreen />;
  }

  if (showSeatsChart) {
    return (
      <>
        <EventImageLayout
          title={`Booking ${eventData?.general?.name}`}
          image={eventData?.general?.images[0]}
          fields={eventData?.general?.fields}
          useFullHeight={true}
          preferredWidth="xxl"
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              marginTop: "88px",
            }}
          >
            <Iconify
              icon="eva:chevron-left-fill"
              width={40}
              sx={{ zIndex: 2, cursor: "pointer" }}
              onClick={async () => {
                await pickerRef.current?.clearSelection();
                setShowSeatsChart(false);
              }}
            />
            <Typography
              variant="h5"
              color={theme.palette.text.primary}
              sx={{ zIndex: 2, mx: "auto" }}
            >
              {translate(`bookingWizard.seatsTitle`)}
            </Typography>
          </Box>

          {chartEvent ? (
            <ChartEventSeatPicker
              ref={pickerRef}
              chartEventId={chartEvent.id}
              chartId={chartEvent.chartId}
              holdTtlMinutes={chartEvent.holdTtlMinutes}
              ticketTypes={ticketTypes}
              quantities={pickerQuantities}
              currency={currency}
              onBudgetChange={(b) => updateTicketQuantities((prev) => budgetToQuantities(b, prev, ticketTypes))}
              onSelectionChange={({ isComplete }) => setUserSelectedAllSeats(isComplete)}
            />
          ) : (
            <SeatsChart
              eventChartKey={eventData?.general.seatsIoEventKey}
              maxSelectableSeats={maxSelectableObjects}
              seatingMapPricing={seatingMapPricing}
              onChartRendered={(chart) => { setChart(chart); }}
              setUserSelectedAllSeats={setUserSelectedAllSeats}
            />
          )}

          <StickyFooter>
            <Box
              sx={{
                textAlign: "center",
              }}
            >
              <PrimaryLoadingButton
                size="large"
                variant="contained"
                loading={isLoadingButton}
                disabled={
                  (!ticketCount && !selectedBookingConfig && !menuCount) ||
                  !userSelectedAllSeats ||
                  !chart
                }
                sx={{
                  width: isMobile ? "calc(100% - 48px)" : "200px",
                  margin: isMobile ? "16px 24px 24px 24px" : "auto",
                }}
                onClick={() => doBuy()}
              >
                {translate("productPage.buyNow")} · {totalCost ? `${fCurrency(totalCost)} ${currency}` : translate('eventManager.free')}
              </PrimaryLoadingButton>
            </Box>
          </StickyFooter>
        </EventImageLayout>
        <DeleteForeignTicketsModal
          modalOpen={clearCartModalOpen}
          setModalOpen={setClearCartModalOpen}
          confirmDeleteCallback={() => doBuy(true)}
          isLoading={isLoadingButton}
        />
      </>
    );
  }

  if (showBooking) {
    let bookingToEdit = { areaId: showBooking };
    if (selectedBookingConfig?.areaId === showBooking) {
      bookingToEdit = { ...selectedBookingConfig };
    }
    return (
      <BookingPage
        eventId={eventData?.general.id}
        backHandler={() => setShowBooking(false)}
        removeHandler={removeHandler}
        bookingToEdit={bookingToEdit}
        selectedTickets={ticketQuantities}
        selectedMenus={selectedMenus}
        linkedEventStartDate={location?.state?.linkedEventStartDate}
        doneBookingHandler={(bookingConfig) => {
          setShowBooking(false);
          setSelectedBookingConfig(bookingConfig);
          if (
            modules.includes("bookingMenu") &&
            eventData.booking.menus.length
          ) {
            setActiveAccordion("bookingMenu");
          }
        }}
      />
    );
  }
  const areaType = eventData?.booking?.areas;
  const hooldingFee = eventData?.booking?.holdingFee;

  return (
    <EventImageLayout
      title={`Booking ${eventData?.general?.name}`}
      image={eventData?.general?.images[0]}
      fields={eventData?.general?.fields}
      useFullHeight={true}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-start",
          marginTop: "88px",
        }}
      >
        <Iconify
          icon="eva:chevron-left-fill"
          width={40}
          sx={{ zIndex: 2, cursor: "pointer" }}
          onClick={() => navigate(-1)}
        />
        <Typography
          variant="h5"
          color={theme.palette.text.primary}
          sx={{ zIndex: 2, mx: "auto" }}
        >
          {translate(`bookingWizard.title`)}
        </Typography>
      </Box>
      {modules.includes("tickets") && (
        <BookingWizardAccordion
          type={"tickets"}
          title={"tickets"}
          activeAccordion={activeAccordion}
          setActiveAccordion={setActiveAccordion}
          summary={
            !!ticketCount &&
            `${ticketCount} ${ticketCount === 1
              ? translate("checkout.item")
              : translate("checkout.items")
            }`
          }
        >
          {getTicketCard(
            theme,
            translate,
            false,
            eventData,
            ticketQuantities,
            updateTicketQuantities
          )}
        </BookingWizardAccordion>
      )}
      {modules.includes("booking") && areaType && (
        <BookingWizardAccordion
          type={"booking"}
          title={eventData?.booking?.isBookableUnit ? "options" : "booking"}
          activeAccordion={activeAccordion}
          setActiveAccordion={setActiveAccordion}
          summary={!!selectedBookingConfig && `1 ${translate("checkout.item")}`}
        >
          {areaType.map((area) => (
            <ProductCard
              key={area.id}
              name={area.name}
              price={fCommission(area.price || hooldingFee, eventData?.booking?.comissionHoldingFee, eventData?.general?.commissionPaidByUser)}
              description={area.description}
              currency={currency}
              sx={{
                borderRadius: "8px",
              }}
            >
              <PrimaryButton
                variant="contained"
                disabled={selectedBookingConfig?.areaId && selectedBookingConfig?.areaId !== area.id}
                onClick={() => handleSelectArea(area.id)}
              >
                {selectedBookingConfig?.areaId === area.id
                  ? translate("checkout.changeQty")
                  : translate("bookings.newReservation")}
              </PrimaryButton>
            </ProductCard>
          ))}
        </BookingWizardAccordion>
      )}
      {modules.includes("bookingMenu") && eventData.booking.menus.length ? (
        <BookingWizardAccordion
          type={"bookingMenu"}
          title={"bookingMenu"}
          activeAccordion={activeAccordion}
          setActiveAccordion={setActiveAccordion}
          summary={
            !!menuCount &&
            `${menuCount} ${menuCount === 1
              ? translate("checkout.item")
              : translate("checkout.items")
            }`
          }
        >
          {Object.keys(menuCategories).map((menuCategory) => {
            const isOpen = expandedMenuCats.has(menuCategory);
            return (
              <Accordion
                key={menuCategory}
                expanded={isOpen}
                onChange={() => toggleMenuCategory(menuCategory)}
                disableGutters
                square
                sx={flatAccordion}
              >
                <AccordionSummary sx={noJumpSummary}>
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.25 }}>
                    <Typography variant="h6" component="div">
                      {getMenuCategoryTitle(menuCategory)}
                    </Typography>
                    <ExpandMoreIcon
                      sx={{
                        transition: "transform 150ms ease",
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      }}
                    />
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0, pb: 2 }}>
                  {menuCategories[menuCategory].map((menu) => (
                    <ProductCard
                      key={menu.id}
                      name={menu.name}
                      price={fCommission(menu.price, eventData?.booking?.comissionHoldingFee, eventData?.general?.commissionPaidByUser)}
                      description={menu.description}
                      currency={currency}
                      quantityString={
                        menu.quantity + " " +
                        translate(`eventManager.menus.quantityMetric.${menu.quantityMetric}`)
                      }
                      sx={{ background: "transparent", boxShadow: "none", border: "none" }}
                    >
                      {eventData?.booking?.enabledMenuPurchase ? (
                        <Incrementer
                          disabled={!selectedBookingConfig}
                          quantity={
                            selectedMenus?.find((it) => it.id === menu.id)?.quantity || 0
                          }
                          onDecrementQuantity={() => {
                            let menus = [...selectedMenus];
                            const existing = menus.find((it) => it.id === menu.id);
                            if (existing) {
                              existing.quantity -= 1;
                              if (!existing.quantity) menus = menus.filter((it) => it.id !== menu.id);
                            }
                            setSelectedMenus([...menus]);
                          }}
                          onIncrementQuantity={() => {
                            let menus = [...selectedMenus];
                            const existing = menus.find((it) => it.id === menu.id);
                            if (existing) existing.quantity += 1;
                            else menus.push({ ...menu, quantity: 1 });
                            setSelectedMenus([...menus]);
                          }}
                        />
                      ) : null}
                    </ProductCard>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}



        </BookingWizardAccordion>
      ) : null}

      <StickyFooter>
        <Box
          sx={{
            textAlign: "center",
          }}
        >
          <PrimaryLoadingButton
            size="large"
            variant="contained"
            loading={isLoadingButton}
            disabled={!ticketCount && !selectedBookingConfig && !menuCount}
            sx={{
              width: isMobile ? "calc(100% - 48px)" : "200px",
              margin: isMobile ? "16px 24px 24px 24px" : "auto",
            }}
            onClick={() =>
              shouldRedirectToSeats ? setShowSeatsChart(true) : doBuy()
            }
          >
            {translate("productPage.buyNow")} · {totalCost ? `${fCurrency(totalCost)} ${currency}` : translate('eventManager.free')}
          </PrimaryLoadingButton>
        </Box>
      </StickyFooter>

      <DeleteForeignTicketsModal
        modalOpen={clearCartModalOpen}
        setModalOpen={setClearCartModalOpen}
        confirmDeleteCallback={() => doBuy(true)}
        isLoading={isLoadingButton}
      />
    </EventImageLayout>
  );
}

function BookingWizardAccordion({
  type,
  title,
  children,
  activeAccordion,
  setActiveAccordion,
  summary,
}) {
  const theme = useTheme();
  const { translate } = useLocales();

  return (
    <Accordion
      expanded={activeAccordion === type}
      onChange={() => {
        setActiveAccordion(activeAccordion !== type ? type : null);
      }}
      sx={accordionStyle}
    >
      <AccordionSummary sx={accordionSummaryStyle}>
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(90deg, #000000 0%, rgba(0, 0, 0, 0.1) 100%), url(/assets/booking/${type}.jpg)`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            zIndex: 1,
            willChange: "transform",
            transform: "translateZ(0)",
          }}
        />
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            height: "80px",
            zIndex: 2,
            px: 2,
          }}
        >
          <Box>
            <Typography
              variant="h5"
              fontWeight={theme.typography.fontWeightRegular}
              color="white"
            >
              {translate(`bookingWizard.${title}`)}
            </Typography>
            {summary && (
              <Typography
                variant="caption"
                color="white"
                fontWeight={theme.typography.fontWeightLight}
              >
                {summary}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              transform:
                activeAccordion === type ? "rotate(0deg)" : "rotate(180deg)",
              mr: 2,
              transition: "transform 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
              color: "inherit",
            }}
          >
            <ExpandMoreIcon sx={{ color: "white" }} />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

const accordionStyle = {
  borderRadius: 1,
  overflow: "hidden",
  backgroundColor: "transparent",
  my: 2,
  "&:before": { display: "none" },

  "&.MuiAccordion-rounded": {
    boxShadow: "none",
    my: 4,
  },
  "&.Mui-expanded": {
    backgroundColor: "transparent",
  },
};


const accordionSummaryStyle = {
  position: "relative",
  height: "80px",
  p: 0,
  borderRadius: 1,
  overflow: "hidden",
  transform: "translateZ(0)",
  WebkitTransform: "translateZ(0)",
  ".MuiAccordionSummary-content": {
    position: "relative",
    zIndex: 2,
  },
};

const flatAccordion = {
  '&&': {
    boxShadow: 'none',
    backgroundColor: 'transparent',
    borderRadius: 0,
    m: 0,
  },
  '&:before, &::before': { display: 'none' },

  '&&.Mui-expanded': {
    boxShadow: 'none',
    backgroundColor: 'transparent',
    borderRadius: 0,
    margin: 0,
  },
};

const noJumpSummary = {
  px: 0,
  minHeight: 56,
  alignItems: 'center',
  '&.Mui-expanded': { minHeight: 56 },
  '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded': {
    margin: 0,
  },
  '&:hover': { backgroundColor: 'transparent' },
}


